/**
 * 주차면 렌더용 최소 면적 회전 사각형.
 *
 * CAD 주차면 엔티티에는 내부 장식 사선 꼭짓점이 섞여 있어 외곽 폴리곤을
 * 그대로 그릴 수 없다(csvRenderer 의 isParkingType 분기가 존재하는 이유).
 * 축 정렬 bbox 로 퉁치면 대각선 배치 칸이 커다란 정사각형으로 부풀어 이웃과
 * 겹쳐 보였다 — hull 을 감싸는 최소 회전 사각형은 사선을 버리면서 기울기는
 * 유지한다. 축 정렬 칸의 결과는 기존 bbox 와 동일하다.
 */

interface Pt {
  x: number
  y: number
}

/** monotone chain convex hull (좌표 오름차순 정렬 후 상/하 체인). */
function convexHull(points: Pt[]): Pt[] {
  const pts = [...points].sort((a, b) => a.x - b.x || a.y - b.y)
  if (pts.length < 3) return pts
  const cross = (o: Pt, a: Pt, b: Pt) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x)
  const build = (list: Pt[]) => {
    const out: Pt[] = []
    for (const p of list) {
      while (out.length >= 2 && cross(out[out.length - 2], out[out.length - 1], p) <= 0) out.pop()
      out.push(p)
    }
    out.pop()
    return out
  }
  return [...build(pts), ...build([...pts].reverse())]
}

/** 점들을 감싸는 최소 면적 회전 사각형의 4귀퉁이 (시계/반시계 순). */
export function minAreaRect(points: Pt[]): Pt[] {
  const hull = convexHull(points)
  if (hull.length < 3) {
    const xs = points.map((p) => p.x)
    const ys = points.map((p) => p.y)
    const x0 = Math.min(...xs)
    const x1 = Math.max(...xs)
    const y0 = Math.min(...ys)
    const y1 = Math.max(...ys)
    return [
      { x: x0, y: y0 },
      { x: x1, y: y0 },
      { x: x1, y: y1 },
      { x: x0, y: y1 },
    ]
  }
  // ponytail: hull 변마다 전 꼭짓점 투영하는 O(n²) — 주차면은 꼭짓점 수십 개라 충분
  let best: Pt[] = []
  let bestArea = Infinity
  for (let i = 0; i < hull.length; i++) {
    const a = hull[i]
    const b = hull[(i + 1) % hull.length]
    const len = Math.hypot(b.x - a.x, b.y - a.y)
    if (len === 0) continue
    const ux = (b.x - a.x) / len
    const uy = (b.y - a.y) / len
    let minU = Infinity
    let maxU = -Infinity
    let minV = Infinity
    let maxV = -Infinity
    for (const p of hull) {
      const u = (p.x - a.x) * ux + (p.y - a.y) * uy
      const v = -(p.x - a.x) * uy + (p.y - a.y) * ux
      minU = Math.min(minU, u)
      maxU = Math.max(maxU, u)
      minV = Math.min(minV, v)
      maxV = Math.max(maxV, v)
    }
    const area = (maxU - minU) * (maxV - minV)
    if (area < bestArea) {
      bestArea = area
      const corner = (u: number, v: number): Pt => ({
        x: a.x + u * ux - v * uy,
        y: a.y + u * uy + v * ux,
      })
      best = [corner(minU, minV), corner(maxU, minV), corner(maxU, maxV), corner(minU, maxV)]
    }
  }
  return best
}
